import {
  Encodable,
  AllocationData,
  Deal,
  OrderData,
  OrderDataImpl,
  DealImpl,
  AllocationDataImpl,
  Long,
  OpsIntEvent,
  Relationship,
  Moment,
} from "../cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
import { GetQuickFilterTextParams, ValueFormatterParams } from "ag-grid-community";

const logger = LoggerService.for(__dirname);

export interface BfxConfigIDRefHelperOptions {
  generate?: boolean;
  config?: {
    orderIDPrefix: string;
    strategyIDPrefix: string;
    tradeIDPrefix: string;
    allocationIDPrefix: string;
    /**
     * min: 1 (e.g. an order id of 1)
     * max: 20 (e.g. an order id of 00000000000000000001 )
     */
    idNumericLength: number;
  };
  minWs?: number;
  digitsWs?: number;
}

/**
 * server keeps track of configured id and handles expansion
 * formatting is centralized in case of future complexity or
 * UI side generation of these references
 */
export class BfxConfigIDRefHelper {
  static readonly defaultOpts: BfxConfigIDRefHelperOptions = {
    minWs: 6,
    digitsWs: 0.4,
  };

  static hasValue(params: ValueFormatterParams | GetQuickFilterTextParams) {
    return (
      params != null &&
      typeof params === "object" &&
      params.data != null &&
      typeof params.data === "object"
    );
  }

  static getActivationDt(
    encodable: OrderData,
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): Moment {
    if (encodable.activationDts != null) return encodable.activationDts;
    return null;
  }

  static allocationRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (BfxConfigIDRefHelper.hasValue(params)) {
      return BfxConfigIDRefHelper.getAllocationRef(params.data);
    }
  };

  static interventionOrderRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (BfxConfigIDRefHelper.hasValue(params) && params.data.orderData != null) {
      return BfxConfigIDRefHelper.getOrderRef(params.data.orderData);
    }
  };

  static orderRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (params.data?.isLadderAggregate) {
      return "";
    }

    if (BfxConfigIDRefHelper.hasValue(params)) {
      return BfxConfigIDRefHelper.getOrderRef(params.data);
    }
  };

  static orderRefValueFormatterEmpty = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    let value: string = null;

    if (BfxConfigIDRefHelper.hasValue(params)) {
      value = BfxConfigIDRefHelper.getOrderRef(params.data);
    }

    return value != null ? value : "-";
  };

  static orderAllocationRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (
      BfxConfigIDRefHelper.hasValue(params) &&
      Array.isArray(params.data.allocationData) &&
      params.data.allocationData.length > 0
    ) {
      return BfxConfigIDRefHelper.getOrderAllocationRef(params.data);
    }
  };

  static orderAllocationDealRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (
      BfxConfigIDRefHelper.hasValue(params) &&
      Array.isArray(params.data.allocationData) &&
      params.data.allocationData.length > 0
    ) {
      return BfxConfigIDRefHelper.getOrderAllocationDealRef(params.data);
    }
  };

  static dealAggregationRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (BfxConfigIDRefHelper.hasValue(params)) {
      return BfxConfigIDRefHelper.getDealAggregateRef(params.data);
    }
  };

  static dealRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (BfxConfigIDRefHelper.hasValue(params)) {
      return BfxConfigIDRefHelper.getDealRef(params.data);
    }
  };

  static strategyRefValueFormatter = function (
    this: null,
    params: ValueFormatterParams | GetQuickFilterTextParams,
  ) {
    if (BfxConfigIDRefHelper.hasValue(params)) {
      return BfxConfigIDRefHelper.getStrategyRef(params.data);
    }
  };

  static getAllocationRef(
    encodable: AllocationData | Deal,
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string {
    return encodable.allocationRef;
  }

  static getOrderAllocationRef(encodable: OrderData, opts?: BfxConfigIDRefHelper): string {
    return encodable.allocationData[0]["allocationRef"];
  }

  static getOrderAllocationDealRef(encodable: OrderData, opts?: BfxConfigIDRefHelper): string {
    return encodable.allocationData[0].fillDetails["dealRef"];
  }

  static getOpsIntOrderRef(
    encodable: OpsIntEvent,
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string | null {
    if (encodable != null && encodable.info != null && encodable.info.orderData != null) {
      return encodable.info.orderData.orderRef;
    }

    return null;
  }

  static getOpsIntDealRef(
    encodable: OpsIntEvent,
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string | null {
    if (encodable != null && encodable.info != null && encodable.info.deal != null) {
      return encodable.info.deal.dealRef;
    }

    return null;
  }

  static getOrderRef(
    encodable: OrderData | Deal | Relationship | { orderId: Long | string; orderRef: string },
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string {
    return encodable.orderRef;
  }

  static getDealAggregateRef(
    encodable:
      | Deal
      | { id?: Long | string; dealId?: Long | string; dealRef: string; aggregateRef: string },
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string {
    return encodable.aggregateRef || "";
  }

  static getDealRef(
    encodable: Deal | { id?: Long | string; dealId?: Long | string; dealRef: string },
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string {
    return encodable.dealRef;
  }

  static getStrategyRef(
    encodable: OrderData | { strategyId: Long | string; strategyRef: string },
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string {
    return encodable.strategyRef;
  }

  private static calcWs(numWs: number) {
    if (numWs <= 1) return 29;

    return 4 + numWs * 15;
  }

  static getWs(
    prefixLength: number,
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ) {
    let idNumericLength = opts.config != null ? opts.config.idNumericLength : 1;
    let ws = prefixLength + idNumericLength * opts.digitsWs;

    if (ws > opts.minWs) {
      return BfxConfigIDRefHelper.calcWs(Math.ceil(ws));
    }

    return BfxConfigIDRefHelper.calcWs(opts.minWs);
  }

  static getAllocationRefWs(
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): number {
    let prefixLength = opts.config != null ? opts.config.allocationIDPrefix.length : 0;
    return BfxConfigIDRefHelper.getWs(prefixLength, opts);
  }

  static getOrderRefWs(
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): number {
    let prefixLength = opts.config != null ? opts.config.orderIDPrefix.length : 0;
    return BfxConfigIDRefHelper.getWs(prefixLength, opts);
  }

  static getDealRefWs(
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): number {
    let prefixLength = opts.config != null ? opts.config.tradeIDPrefix.length : 0;
    return BfxConfigIDRefHelper.getWs(prefixLength, opts);
  }

  static getStrategyRefWs(
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): number {
    let prefixLength = opts.config != null ? opts.config.strategyIDPrefix.length : 0;
    return BfxConfigIDRefHelper.getWs(prefixLength, opts);
  }

  static getRef(
    encodable: Encodable,
    opts: BfxConfigIDRefHelperOptions = BfxConfigIDRefHelper.defaultOpts,
  ): string {
    if (
      AllocationDataImpl.enType === encodable.encodableType &&
      encodable instanceof AllocationDataImpl
    ) {
      return BfxConfigIDRefHelper.getAllocationRef(encodable);
    }

    if (OrderDataImpl.enType === encodable.encodableType && encodable instanceof OrderDataImpl) {
      return BfxConfigIDRefHelper.getOrderRef(encodable);
    }

    if (DealImpl.enType === encodable.encodableType && encodable instanceof DealImpl) {
      return BfxConfigIDRefHelper.getDealRef(encodable);
    }

    logger.out.error(`${BfxConfigIDRefHelper.constructor.name}.getRef() unrecognised Encodable`);

    return null;
  }
}
